import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";

const QR_SIZE = 160;
const QR_SIZE_INLINE = 72;
const QR_SIZE_EXPAND = 96;

/**
 * Accessible QR + match URL + join actions (copy, share, download).
 */
export default function MatchJoinQrPanel({
  shareUrl,
  roomLabel,
  onCopyLink,
  /** @default "Scan to open the join link" */
  scanHint,
  /** @default true */
  showShareButton = true,
  onShare,
  onDownloadQr,
  compact = false,
  layout = "default",
  canvasRef: externalCanvasRef,
  /** Override canvas size in px (e.g. lobby invite beside action buttons). */
  qrSize,
}) {
  const internalRef = useRef(null);
  const canvasRef = externalCanvasRef || internalRef;
  const size =
    typeof qrSize === "number" && qrSize > 0
      ? qrSize
      : layout === "horizontal"
        ? QR_SIZE_EXPAND
        : compact
          ? QR_SIZE_INLINE
          : QR_SIZE;
  const label = roomLabel
    ? `QR code to join match ${roomLabel}`
    : "QR code to join this match";

  const handleCopy = () => {
    if (onCopyLink) onCopyLink();
  };

  const handleShare = () => {
    if (onShare) onShare();
  };

  const handleDownload = () => {
    if (onDownloadQr && canvasRef.current) {
      onDownloadQr(canvasRef.current);
    }
  };

  const hint =
    scanHint ||
    (layout === "horizontal"
      ? "Scan to follow this match live from any device."
      : "Scan to open the join link");

  if (layout === "horizontal") {
    return (
      <div className="match-qr-horizontal" data-testid="match-join-qr-panel">
        <div className="match-join-qr-graphic">
          <QRCodeCanvas
            ref={canvasRef}
            value={shareUrl || " "}
            size={size}
            level="M"
            includeMargin
            bgColor="#ffffff"
            fgColor="#0d1440"
            role="img"
            aria-label={label}
          />
        </div>
        <div className="match-qr-horizontal-text">
          <p className="match-qr-horizontal-title">QR Code</p>
          <p className="match-qr-horizontal-desc">{hint}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`match-join-qr${compact ? " match-join-qr-compact" : ""}`}
      data-testid="match-join-qr-panel"
    >
      <div className="match-join-qr-graphic">
        <QRCodeCanvas
          ref={canvasRef}
          value={shareUrl || " "}
          size={size}
          level="M"
          includeMargin
          bgColor="#ffffff"
          fgColor="#0d1440"
          role="img"
          aria-label={label}
        />
      </div>
      {!compact && (
        <>
          <p className="match-join-qr-hint">{hint}</p>
          <p className="match-join-qr-url" title={shareUrl}>
            <span className="sr-only">Match URL: </span>
            {shareUrl}
          </p>
          <div className="match-join-qr-actions">
            <button type="button" className="btn bor bxs match-join-qr-btn" onClick={handleCopy}>
              Copy link
            </button>
            {showShareButton ? (
              <button type="button" className="btn bor bxs match-join-qr-btn" onClick={handleShare}>
                Share
              </button>
            ) : null}
            <button
              type="button"
              className="btn bor bxs match-join-qr-btn"
              onClick={handleDownload}
            >
              Download PNG
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export { QR_SIZE_INLINE };
